import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import type { OwnerEditHook } from '@/shared/types/editHooks';
import type { UpdateOwnerItem } from '@/shared/types/owner';

import type { TeamOwner, UserOwner } from '@/entities/owner';
import { buildNewOwners, buildOwnerOperations, patchOwners } from '@/entities/owner/api';
import { useTeamOwners, useUserOwners } from '@/entities/owner/model';

type UseOwnerEditParams<T> = {
  resourceId: string;
  apiPath: string;
  queryKey: QueryKey;
  getOwnersFromData: (data: T | undefined) => UpdateOwnerItem[];
  invalidateQuery: () => void;
  onSuccess?: () => void;
};

export const useOwnerEdit = <T = unknown>({
  resourceId,
  apiPath,
  queryKey,
  getOwnersFromData,
  invalidateQuery,
  onSuccess,
}: UseOwnerEditParams<T>): OwnerEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('teams');
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const data = queryClient.getQueryData<T>(queryKey);
  const currentOwners = useMemo(() => getOwnersFromData(data), [data, getOwnersFromData]);

  const { data: teamOwners = [], isLoading: isLoadingTeams } = useTeamOwners();
  const { data: userOwners = [], isLoading: isLoadingUsers } = useUserOwners();

  const updateOwnersMutation = useMutation({
    mutationFn: async ({
      selectedTeamId,
      selectedUserIds,
      teams,
      users,
      currentOwners,
    }: {
      selectedTeamId: string | null;
      selectedUserIds: string[];
      teams: TeamOwner[];
      users: UserOwner[];
      currentOwners: UpdateOwnerItem[];
    }) => {
      if (!resourceId) {
        throw new Error('Resource ID is required');
      }

      const newOwners = buildNewOwners({
        selectedTeamId,
        selectedUserIds,
        teams,
        users,
      });
      const operations = buildOwnerOperations({
        newOwners,
        currentOwners,
      });

      await patchOwners({
        resourceId,
        apiPath,
        operations,
      });
    },
  });

  const currentTeamId = useMemo(
    () => currentOwners.find((owner) => owner.type === 'team')?.id || null,
    [currentOwners]
  );

  const currentUserIds = useMemo(() => {
    const ids = currentOwners.filter((o) => o.type === 'user').map((o) => o.id);
    return Array.from(new Set(ids)).sort();
  }, [currentOwners]);

  useEffect(() => {
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
  }, [currentTeamId, currentUserIds]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestData = queryClient.getQueryData<T>(queryKey);
    const latestOwners = getOwnersFromData(latestData);

    if (latestOwners.length > 0) {
      const teamOwner = latestOwners.find((owner) => owner.type === 'team');
      const userOwnerIds = latestOwners.filter((owner) => owner.type === 'user').map((owner) => owner.id);

      if (teamOwner) setSelectedTeamId(teamOwner.id);
      setSelectedUserIds(Array.from(new Set(userOwnerIds)));
    }
  }, [queryClient, queryKey, getOwnersFromData]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSelectedTeamId(currentTeamId);
    setSelectedUserIds(currentUserIds);
  }, [currentTeamId, currentUserIds]);

  const handleTeamSelect = useCallback((teamId: string) => {
    setSelectedTeamId((prev) => (prev === teamId ? null : teamId));
    setSelectedUserIds([]);
  }, []);

  const handleUserSelect = useCallback((userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => (checked ? [...prev, userId] : prev.filter((id) => id !== userId)));
    setSelectedTeamId(null);
  }, []);

  const handleUpdate = useCallback(() => {
    const latestData = queryClient.getQueryData<T>(queryKey);
    const latestOwners = getOwnersFromData(latestData);

    updateOwnersMutation.mutate(
      {
        selectedTeamId,
        selectedUserIds,
        teams: teamOwners,
        users: userOwners,
        currentOwners: latestOwners,
      },
      {
        onSuccess: () => {
          invalidateQuery();
          notification.success({
            message: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS,
            description: NOTIFICATION_MESSAGES.OWNER_UPDATE_SUCCESS_DESC,
            duration: 2,
            placement: 'bottomLeft',
          });
          setIsOpen(false);
          onSuccess?.();
        },
        onError: (error) => {
          notification.error({
            message: NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR,
            description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.OWNER_UPDATE_ERROR_DESC,
            duration: 3,
            placement: 'bottomLeft',
          });
        },
      }
    );
  }, [
    selectedTeamId,
    selectedUserIds,
    teamOwners,
    userOwners,
    queryClient,
    queryKey,
    getOwnersFromData,
    invalidateQuery,
    updateOwnersMutation,
    onSuccess,
  ]);

  const hasChanges = useMemo(() => {
    const teamChanged = currentTeamId !== selectedTeamId;
    const usersChanged =
      currentUserIds.length !== selectedUserIds.length || !currentUserIds.every((id) => selectedUserIds.includes(id));

    return teamChanged || usersChanged;
  }, [currentTeamId, currentUserIds, selectedTeamId, selectedUserIds]);

  return {
    isOpen,
    activeTab,
    selectedTeamId,
    selectedUserIds,
    teamOwners,
    userOwners,
    currentOwners,
    isLoadingTeams,
    isLoadingUsers,
    isUpdating: updateOwnersMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    setActiveTab,
    handleTeamSelect,
    handleUserSelect,
    handleUpdate,
  };
};
